/**
 * Der „+"-Knopf der mobilen Leiste: welches Projekt ist gemeint?
 *
 * Bis 09/2026 fragte App.tsx den Standort ohne Frist ab und oeffnete dann
 * ungefragt das naechste Projekt unter 0,5 km — ohne Rueckmeldung, solange
 * gesucht wurde, und bei mehreren Losen an einer Adresse irgendeines davon.
 * Jetzt entscheidet der Nutzer: ein Treffer wird bestaetigt, mehrere stehen
 * zur Wahl, kein Treffer oder kein Standort fuehrt wie bisher in die
 * Projektauswahl — mit einem Satz, warum.
 *
 * Rein und ohne React: Auswahl, Reihenfolge und Texte sind ohne DOM pruefbar.
 * Die Haversine-Formel kommt aus nearbyProjects und wird nicht erneut
 * ausgeschrieben.
 */
import { calculateDistanceKm } from './nearbyProjects';
import { isProjectFinished } from './projectBilling';

/**
 * Vertrag V2: vor der Standortsuche fragt App.tsx die offene Ansicht, ob sie
 * den neuen Eintrag selbst anlegt (Pruefmodul → Mangel-Assistent). Wer ihn
 * uebernimmt, ruft `preventDefault()`.
 */
export const NEUER_EINTRAG_EREIGNIS = 'osia:neuer-eintrag';

/** Sendet das Ereignis; true = eine Ansicht hat den Eintrag uebernommen. */
export const meldeNeuerEintrag = (ziel: EventTarget): boolean =>
    !ziel.dispatchEvent(new CustomEvent(NEUER_EINTRAG_EREIGNIS, { cancelable: true }));

/** Umkreis, in dem ein Projekt als „hier" gilt (wie bisher). */
export const PLUS_UMKREIS_KM = 0.5;

/**
 * Standortabfrage: nach 10 s aufgeben statt endlos zu warten; eine hoechstens
 * eine Minute alte Position genuegt. Hohe Genauigkeit wie beim Dashboard
 * (DashboardView, „in der Nähe") — bei 500 m Umkreis zaehlt sie.
 */
export const PLUS_STANDORT_OPTIONEN: PositionOptions = { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 };

/** Was „+" von einer Zeile der Projektliste braucht. */
export interface PlusProjekt {
    id: string;
    name?: string | null;
    status?: string | null;
    data?: {
        metadata?: {
            projectName?: string | null;
            projectAddress?: string | null;
            globalData?: {
                status?: string | null;
                locationCoordinates?: { lat?: number | string | null; lng?: number | string | null } | null;
            } | null;
        } | null;
    } | null;
}

export interface PlusTreffer<P> {
    projekt: P;
    name: string;
    adresse: string;
    km: number;
}

/** Das Blatt ueber der Leiste: suchen, einen Treffer bestaetigen, unter mehreren waehlen. */
export type PlusBlatt<P> =
    | { art: 'suche' }
    | { art: 'bestaetigen'; treffer: PlusTreffer<P> }
    | { art: 'auswaehlen'; treffer: PlusTreffer<P>[] };

/** Was nach der Standortsuche geschieht; 'liste' = bisherige Projektauswahl. */
export type PlusSchritt<P> =
    | Exclude<PlusBlatt<P>, { art: 'suche' }>
    | { art: 'liste'; hinweis: string };

// Nur Projekte MIT Koordinaten koennen „in der Naehe" sein — ein Projekt vor
// Ort ohne Geodaten soll nicht nach GPS-Fehler aussehen.
export const KEIN_PROJEKT_IN_DER_NAEHE = 'Kein Projekt mit hinterlegtem Standort in der Nähe — bitte Projekt wählen.';

/** Satz im Blatt, wenn eine offene Maske (Entwurf, Upload, Versand) den Wechsel verbietet. */
export const PLUS_OFFENE_EINGABE = 'Erst offene Eingabe abschließen oder Vorgang abwarten.';

/**
 * Nadeln, die naeher beieinander liegen, sind ein Ort: Lose an einer
 * Adresse, „1" und „1a", eine von Hand versetzte Nadel.
 */
export const PLUS_GLEICHER_ORT_KM = 0.02;

/**
 * Projekte im Umkreis von `lat`/`lng`, naechstes zuerst.
 *
 * Heraus faellt, was beendet oder archiviert ist ('completed'/'finished',
 * seit 06.10.2026 beides — wie in der Projektliste) oder keine Koordinaten
 * hat; 0 zaehlt als „keine", wie im Dashboard. Projekte an einem Ort (Nadeln unter 20 m auseinander, meist Lose an einer Adresse) stehen
 * beisammen und nach Name, Zahlen natuerlich sortiert („Los 2" vor „Los 10").
 * Verglichen werden die Nadeln untereinander, nicht ihr Abstand zum Nutzer:
 * sonst hinge die Reihenfolge davon ab, wo er gerade steht.
 */
export const plusTreffer = <P extends PlusProjekt>(
    projekte: ReadonlyArray<P>,
    lat: number,
    lng: number,
    umkreisKm: number = PLUS_UMKREIS_KM,
): PlusTreffer<P>[] => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
    const kandidaten: Array<{ treffer: PlusTreffer<P>; lat: number; lng: number }> = [];
    for (const projekt of projekte) {
        if (!projekt) continue;
        const meta = projekt.data?.metadata;
        if (isProjectFinished(projekt)) continue;
        const c = meta?.globalData?.locationCoordinates;
        const pLat = Number(c?.lat);
        const pLng = Number(c?.lng);
        if (!pLat || !pLng || !Number.isFinite(pLat) || !Number.isFinite(pLng)) continue;
        const km = calculateDistanceKm(lat, lng, pLat, pLng);
        if (!(km < umkreisKm)) continue;
        kandidaten.push({
            treffer: {
                projekt,
                name: String(projekt.name || meta?.projectName || 'Unbenanntes Projekt'),
                adresse: String(meta?.projectAddress || ''),
                km,
            },
            lat: pLat,
            lng: pLng,
        });
    }
    const nachName = (a: PlusTreffer<P>, b: PlusTreffer<P>) =>
        a.name.localeCompare(b.name, 'de', { numeric: true }) || a.km - b.km;
    // Naechster zuerst; jeder schliesst sich dem ersten Ort an, dessen
    // naechstgelegene Nadel keine 20 m von seiner entfernt ist.
    kandidaten.sort((a, b) => a.treffer.km - b.treffer.km || nachName(a.treffer, b.treffer));
    const orte: Array<{ lat: number; lng: number; treffer: PlusTreffer<P>[] }> = [];
    for (const k of kandidaten) {
        const ort = orte.find(o => calculateDistanceKm(o.lat, o.lng, k.lat, k.lng) < PLUS_GLEICHER_ORT_KM);
        if (ort) ort.treffer.push(k.treffer);
        else orte.push({ lat: k.lat, lng: k.lng, treffer: [k.treffer] });
    }
    return orte.flatMap(o => o.treffer.sort(nachName));
};

/** 0 Treffer → Projektauswahl mit Hinweis, 1 → bestaetigen, mehrere → auswaehlen. */
export const plusSchrittNachStandort = <P extends PlusProjekt>(
    projekte: ReadonlyArray<P>,
    lat: number,
    lng: number,
): PlusSchritt<P> => {
    const treffer = plusTreffer(projekte, lat, lng);
    if (treffer.length === 0) return { art: 'liste', hinweis: KEIN_PROJEKT_IN_DER_NAEHE };
    if (treffer.length === 1) return { art: 'bestaetigen', treffer: treffer[0] };
    return { art: 'auswaehlen', treffer };
};

/**
 * Was ein Tipp auf einen Treffer im Blatt ausloest.
 *
 *  - 'oeffnen':    anderes Projekt → wie in der Projektauswahl oeffnen.
 *  - 'hub':        das schon geladene Projekt → NICHT neu oeffnen (Sperre,
 *                  Ladebildschirm: eine Schleife statt einer Erfassung),
 *                  sondern in seinen Hub. Nicht direkt in die Begehung: ob ein
 *                  Entwurf offen ist, der letzte Bericht schon versendet, eine
 *                  Begruendung aussteht oder der Bericht deaktiviert ist, weiss
 *                  nur der Begehungsknopf des Hubs (resolveBegehungsKnopf). Ein
 *                  Sprung ins Modul legte neue Maengel sonst still in den
 *                  versendeten Bericht.
 *  - 'schliessen': dessen Begehung steht schon offen → nur das Blatt zu.
 *  - 'warten':     eine offene Maske (idleHold) ginge beim Wechsel verloren
 *                  → nicht wechseln, Hinweis PLUS_OFFENE_EINGABE.
 */
export type PlusOeffnenSchritt = 'oeffnen' | 'hub' | 'schliessen' | 'warten';

export const plusOeffnenSchritt = (o: {
    trefferId: string;
    /** Das geladene Projekt (Bericht da), sonst null. */
    offenesProjektId: string | null;
    /** Steht gerade die Begehung (Pruefmodul) offen? */
    inBegehung: boolean;
    /** Haelt eine Maske den Leerlauf-Rueckzug auf (isIdleExitHeld)? */
    eingabeOffen: boolean;
}): PlusOeffnenSchritt => {
    const offen = o.offenesProjektId !== null && o.trefferId === o.offenesProjektId;
    if (offen && o.inBegehung) return 'schliessen';
    if (o.eingabeOffen) return 'warten';
    return offen ? 'hub' : 'oeffnen';
};

/**
 * Kein Standort → Projektauswahl wie bisher, mit einem Satz, warum.
 * `code` aus GeolocationPositionError: 1 verweigert, 2 nicht ermittelbar,
 * 3 Zeit abgelaufen; ohne Code (kein Geolocation im Geraet) wie 2.
 */
export const plusStandortFehler = (code?: number): string => {
    if (code === 1) return 'Standort nicht freigegeben — bitte Projekt wählen.';
    if (code === 3) return 'Standort nicht rechtzeitig gefunden — bitte Projekt wählen.';
    return 'Standort nicht verfügbar — bitte Projekt wählen.';
};

/** Entfernung fuer die Anzeige: unter 100 m „vor Ort", unter 1 km in Metern (auf 10 m), sonst km. */
export const plusEntfernung = (km: number): string => {
    if (!Number.isFinite(km) || km < 0) return '';
    if (km < 0.1) return 'vor Ort';
    if (km < 1) return `${Math.round(km * 100) * 10} m entfernt`;
    return `${km.toFixed(1).replace('.', ',')} km entfernt`;
};
